import { useEffect, useState, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useThemeContext } from '@/contexts/ThemeContext';

const DARK_TILES = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png';
const LIGHT_TILES = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
const SATELLITE_TILES = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const LABELS_OVERLAY = 'https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png';
const ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>';
const SAT_ATTR = 'Tiles &copy; Esri';
const OSRM = 'https://router.project-osrm.org/route/v1/driving';

// Truck icon - clean SVG without circle wrapper
const truckIcon = (color = '#E5A100') => L.divIcon({
  className: '',
  html: `<div style="width:40px;height:40px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 2px 8px ${color}99) drop-shadow(0 1px 3px rgba(0,0,0,0.4));">
    <img src="/icons/truck_new.svg" style="width:36px;height:36px;object-fit:contain;" alt="truck" />
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

const pickupIcon = L.divIcon({
  className: '',
  html: `<div style="width:32px;height:32px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 2px 4px rgba(220,38,38,0.5));">
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="8" fill="#DC2626" stroke="white" stroke-width="2"/><circle cx="12" cy="12" r="3" fill="white"/></svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

const deliveryIcon = L.divIcon({
  className: '',
  html: `<div style="width:28px;height:40px;display:flex;align-items:center;justify-content:center;filter:drop-shadow(0 2px 6px rgba(220,38,38,0.5));">
    <svg width="28" height="40" viewBox="0 0 28 40" fill="none"><path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.27 21.73 0 14 0z" fill="#DC2626"/><circle cx="14" cy="14" r="6" fill="white"/></svg>
  </div>`,
  iconSize: [28, 40],
  iconAnchor: [14, 40],
});

// Fetch and render OSRM route between two points
function RouteLine({ from, to, color = '#E5A100', driverPos }) {
  const [routeCoords, setRouteCoords] = useState([]);

  useEffect(() => {
    if (!from?.[0] || !to?.[0]) return;
    (async () => {
      try {
        const res = await fetch(`${OSRM}/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`);
        const data = await res.json();
        if (data.routes?.[0]) {
          setRouteCoords(data.routes[0].geometry.coordinates.map(([lng, lat]) => [lat, lng]));
        } else {
          setRouteCoords([from, to]);
        }
      } catch {
        setRouteCoords([from, to]);
      }
    })();
  }, [from?.[0], from?.[1], to?.[0], to?.[1]]);

  if (routeCoords.length === 0) return null;

  if (driverPos?.[0] && routeCoords.length > 2) {
    const dll = L.latLng(driverPos[0], driverPos[1]);
    let closestIdx = 0, minDist = Infinity;
    routeCoords.forEach((c, i) => {
      const d = dll.distanceTo(L.latLng(c[0], c[1]));
      if (d < minDist) { minDist = d; closestIdx = i; }
    });
    const traveled = routeCoords.slice(0, closestIdx + 1);
    const remaining = routeCoords.slice(closestIdx);
    return (<>
      <Polyline positions={traveled} color={color} weight={5} opacity={0.9} />
      <Polyline positions={remaining} color={color} weight={4} opacity={0.35} dashArray="10 8" />
    </>);
  }

  return <Polyline positions={routeCoords} color={color} weight={4} opacity={0.7} />;
}

function FitBounds({ markers }) {
  const map = useMap();
  const hasFitted = useRef(false);
  const markerCount = markers?.filter(m => m.lat && m.lng).length || 0;

  useEffect(() => {
    // Only fit bounds ONCE on initial load or when marker count changes (new markers added/removed)
    // Don't refit when marker positions update (driver moving)
    const valid = markers?.filter(m => m.lat && m.lng);
    if (!hasFitted.current && valid?.length > 0) {
      hasFitted.current = true;
      if (valid.length > 1) {
        const bounds = L.latLngBounds(valid.map(m => [m.lat, m.lng]));
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      } else {
        map.setView([valid[0].lat, valid[0].lng], 8);
      }
    }
  }, [markerCount, map]);
  return null;
}

// Tile layer switcher — manages base + label layers
function TileSwitcher({ satellite, dark }) {
  const map = useMap();
  useEffect(() => {
    map.eachLayer(layer => {
      if (layer instanceof L.TileLayer) map.removeLayer(layer);
    });
    if (satellite) {
      L.tileLayer(SATELLITE_TILES, { attribution: SAT_ATTR }).addTo(map);
      // Add street name labels on top of satellite imagery
      L.tileLayer(LABELS_OVERLAY, { attribution: ATTR, pane: 'overlayPane' }).addTo(map);
    } else {
      const url = dark ? DARK_TILES : LIGHT_TILES;
      L.tileLayer(url, { attribution: ATTR }).addTo(map);
    }
  }, [satellite, dark, map]);
  return null;
}

// Delivery marker with ETA + full details popup
function DeliveryMarkerWithETA({ marker, driverPos, onViewDetails }) {
  const [eta, setEta] = useState(null);

  const calcEta = useCallback(async () => {
    if (!driverPos?.[0]) return;
    try {
      const res = await fetch(`${OSRM}/${driverPos[1]},${driverPos[0]};${marker.lng},${marker.lat}?overview=false`);
      const data = await res.json();
      if (data.routes?.[0]) {
        const mins = Math.round(data.routes[0].duration / 60);
        const dist = (data.routes[0].distance / 1000).toFixed(1);
        setEta({ minutes: mins, km: dist });
      }
    } catch { /* ignore */ }
  }, [driverPos, marker.lat, marker.lng]);

  return (
    <Marker position={[marker.lat, marker.lng]} icon={deliveryIcon}
      eventHandlers={{ click: calcEta }}>
      <Popup>
        <div className="font-sans text-sm min-w-[200px]">
          <strong>{marker.label}</strong>
          {marker.detail && <p className="text-xs mt-1 text-gray-500">{marker.detail}</p>}
          {marker.loadInfo && (
            <div className="mt-2 space-y-1 text-xs">
              {marker.loadInfo.cargo && <div><span className="text-gray-400">Cargo:</span> {marker.loadInfo.cargo}</div>}
              {marker.loadInfo.weight && <div><span className="text-gray-400">Weight:</span> {marker.loadInfo.weight} lbs</div>}
              {marker.loadInfo.rate && <div><span className="text-gray-400">Rate:</span> ${marker.loadInfo.rate}</div>}
              {marker.loadInfo.status && <div><span className="text-gray-400">Status:</span> {marker.loadInfo.status}</div>}
              {marker.loadInfo.driver && <div><span className="text-gray-400">Driver:</span> {marker.loadInfo.driver}</div>}
              {marker.loadInfo.customer && <div><span className="text-gray-400">Customer:</span> {marker.loadInfo.customer}</div>}
            </div>
          )}
          {eta && (
            <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-xs">ETA</span>
                <span className="font-bold text-sm">
                  {eta.minutes < 60 ? `${eta.minutes} min` : `${Math.floor(eta.minutes/60)}h ${eta.minutes%60}m`}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-xs">Distance</span>
                <span className="font-bold text-sm">{eta.km} km</span>
              </div>
            </div>
          )}
          {onViewDetails && marker.detailId && (
            <button onClick={() => onViewDetails(marker.detailType || 'load', marker.detailId)}
              className="mt-2 w-full text-center text-xs font-medium py-1.5 rounded"
              style={{ color: '#E5A100', background: 'rgba(229,161,0,0.1)' }}>
              View Full Details →
            </button>
          )}
        </div>
      </Popup>
    </Marker>
  );
}

export default function MapView({
  markers = [], loads = [], dark: darkProp, center, zoom = 5,
  className = '', style = {}, showLegend = false, showSatelliteToggle = false,
  onViewDetails
}) {
  const { theme } = useThemeContext();
  const dark = darkProp !== undefined ? darkProp : theme === 'dark';
  const [satellite, setSatellite] = useState(true);
  const defaultCenter = center || [50.0, -85.0];
  const driverMarkers = markers.filter(m => m.type === 'truck');
  const firstDriverPos = driverMarkers.length > 0 ? [driverMarkers[0].lat, driverMarkers[0].lng] : null;

  return (
    <div className="relative" style={{ height: '100%', width: '100%', isolation: 'isolate' }}>
      <MapContainer center={defaultCenter} zoom={zoom} className={className}
        style={{ height: '100%', width: '100%', minHeight: '300px', ...style }}
        scrollWheelZoom={true} data-testid="map-container">
        <TileSwitcher satellite={satellite} dark={dark} />

        {loads.map(ld => {
          if (!ld.pickup_lat || !ld.delivery_lat) return null;
          const driverPos = ld.driver_lat ? [ld.driver_lat, ld.driver_lng] : null;
          return (
            <RouteLine key={`route-${ld.load_id}`}
              from={[ld.pickup_lat, ld.pickup_lng]}
              to={[ld.delivery_lat, ld.delivery_lng]}
              color="#E5A100"
              driverPos={driverPos} />
          );
        })}

        {markers.map((m, i) => {
          if (!m.lat || !m.lng) return null;
          if (m.type === 'delivery') {
            return <DeliveryMarkerWithETA key={m.id || i} marker={m} driverPos={firstDriverPos} onViewDetails={onViewDetails} />;
          }
          if (m.type === 'pickup') {
            return (
              <Marker key={m.id || i} position={[m.lat, m.lng]} icon={pickupIcon}>
                <Popup>
                  <div className="font-sans text-sm min-w-[200px]">
                    <strong>{m.label}</strong>
                    {m.detail && <p className="text-xs mt-1 text-gray-500">{m.detail}</p>}
                    {m.loadInfo && (
                      <div className="mt-2 space-y-1 text-xs">
                        {m.loadInfo.cargo && <div><span className="text-gray-400">Cargo:</span> {m.loadInfo.cargo}</div>}
                        {m.loadInfo.weight && <div><span className="text-gray-400">Weight:</span> {m.loadInfo.weight} lbs</div>}
                        {m.loadInfo.rate && <div><span className="text-gray-400">Rate:</span> ${m.loadInfo.rate}</div>}
                        {m.loadInfo.status && <div><span className="text-gray-400">Status:</span> {m.loadInfo.status}</div>}
                      </div>
                    )}
                    {onViewDetails && m.detailId && (
                      <button onClick={() => onViewDetails(m.detailType || 'load', m.detailId)}
                        className="mt-2 w-full text-center text-xs font-medium py-1.5 rounded"
                        style={{ color: '#E5A100', background: 'rgba(229,161,0,0.1)' }}>
                        View Full Details →
                      </button>
                    )}
                  </div>
                </Popup>
              </Marker>
            );
          }
          // Truck/driver markers
          return (
            <Marker key={m.id || i} position={[m.lat, m.lng]}
              icon={truckIcon(m.color || '#E5A100')}>
              <Popup>
                <div className="font-sans text-sm min-w-[200px]">
                  <strong>{m.label}</strong>
                  {m.detail && <p className="text-xs mt-1 text-gray-500">{m.detail}</p>}
                  {m.driverInfo && (
                    <div className="mt-2 space-y-1 text-xs">
                      {m.driverInfo.phone && <div><span className="text-gray-400">Phone:</span> {m.driverInfo.phone}</div>}
                      {m.driverInfo.truck_type && <div><span className="text-gray-400">Truck:</span> {m.driverInfo.truck_type}</div>}
                      {m.driverInfo.currentLoad && <div><span className="text-gray-400">Current Load:</span> {m.driverInfo.currentLoad}</div>}
                      {m.driverInfo.status && <div><span className="text-gray-400">Status:</span> {m.driverInfo.status}</div>}
                    </div>
                  )}
                  {onViewDetails && m.detailId && (
                    <button onClick={() => onViewDetails(m.detailType || 'driver', m.detailId)}
                      className="mt-2 w-full text-center text-xs font-medium py-1.5 rounded"
                      style={{ color: '#E5A100', background: 'rgba(229,161,0,0.1)' }}>
                      View Full Details →
                    </button>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}

        {markers.length > 0 && <FitBounds markers={markers} />}
      </MapContainer>

      {/* Satellite Toggle */}
      {showSatelliteToggle && (
        <div className="absolute top-3 right-3 z-[1000]">
          <div className="flex bg-card/90 backdrop-blur-sm rounded-lg border border-border overflow-hidden">
            <button onClick={() => setSatellite(false)}
              className={`px-3 py-1.5 text-[10px] font-bold tracking-wider transition-colors ${!satellite ? 'bg-brand-accent/20 text-brand-accent' : 'text-muted-foreground hover:text-foreground'}`}>
              MAP
            </button>
            <button onClick={() => setSatellite(true)}
              className={`px-3 py-1.5 text-[10px] font-bold tracking-wider transition-colors ${satellite ? 'bg-brand-accent/20 text-brand-accent' : 'text-muted-foreground hover:text-foreground'}`}>
              SATELLITE
            </button>
          </div>
        </div>
      )}

      {/* Legend */}
      {showLegend && (
        <div className="absolute bottom-3 left-3 z-[1000] bg-card/90 backdrop-blur-sm rounded-lg border border-border px-3 py-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <img src="/icons/truck_new.svg" alt="Driver" className="w-5 h-5 object-contain" />
            <span className="text-[10px] text-muted-foreground font-medium">Driver</span>
          </div>
          <div className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8" fill="#DC2626" stroke="white" strokeWidth="2"/><circle cx="12" cy="12" r="3" fill="white"/></svg>
            <span className="text-[10px] text-muted-foreground font-medium">Pickup</span>
          </div>
          <div className="flex items-center gap-2">
            <svg width="14" height="20" viewBox="0 0 28 40"><path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.27 21.73 0 14 0z" fill="#DC2626"/><circle cx="14" cy="14" r="6" fill="white"/></svg>
            <span className="text-[10px] text-muted-foreground font-medium">Delivery</span>
          </div>
          <div className="flex items-center gap-2">
            <svg width="20" height="4"><line x1="0" y1="2" x2="20" y2="2" stroke="#E5A100" strokeWidth="3" strokeOpacity="0.9"/></svg>
            <span className="text-[10px] text-muted-foreground font-medium">Traveled</span>
          </div>
          <div className="flex items-center gap-2">
            <svg width="20" height="4"><line x1="0" y1="2" x2="20" y2="2" stroke="#E5A100" strokeWidth="3" strokeOpacity="0.35" strokeDasharray="4 3"/></svg>
            <span className="text-[10px] text-muted-foreground font-medium">Remaining</span>
          </div>
        </div>
      )}
    </div>
  );
}

export { truckIcon, pickupIcon, deliveryIcon };
